<template>
    <div class="modal-backdrop fixed inset-0 bg-black/5 flex items-center justify-center z-[1000]"
        @click.self="closeModal">
        <div
            class="relative bg-white dark:bg-n-slate-2 shadow-lg rounded-xl w-[600px] max-w-[90%] h-[70vh] max-h-[90vh] flex flex-col rtl:text-right skip-context-menu rounded-xl overflow-hidden">
            <div
                class="flex justify-between items-center p-4 border-b-2 border-n-slate-4 dark:border-n-slate-7 flex-shrink-0">
                <div>
                    <h3 class="text-lg font-semibold m-0">{{ title }}</h3>
                    <p v-if="subtitle" class="text-xs text-n-slate-11 mt-1">{{ subtitle }}</p>
                </div>
                <button
                    class="z-10 ltr:right-2 rtl:left-2 top-2 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
                    @click="closeModal">
                    <span class="i-lucide-x flex-shrink-0"></span>
                </button>
            </div>

            <div class="bg-n-slate-1 flex-1 overflow-hidden flex flex-col">
                <div ref="conversationList" class="flex-1 overflow-y-auto custom-scrollbar" @scroll="handleScroll">
                    <div class="flex flex-col gap-2 p-6">
                        <!-- Loading state -->
                        <div v-if="loading" class="flex flex-col items-center justify-center py-12">
                            <span class="animate-spin i-lucide-loader inline-block text-3xl mb-4 text-woot-600"></span>
                            <p class="text-n-slate-11">Carregando conversas...</p>
                        </div>

                        <!-- Content when not loading -->
                        <template v-else>
                            <div v-for="conversation in displayedConversations" :key="conversation.display_id"
                                class="p-3 pr-4 bg-white dark:bg-n-slate-2 border border-n-slate-4 dark:border-n-slate-7 rounded-lg cursor-pointer transition-all duration-200 hover:bg-n-slate-1 hover:border-woot-600 dark:hover:bg-n-slate-3 dark:hover:border-woot-600 flex flex-row justify-between items-center"
                                @click="openConversation(conversation.display_id)">
                                <div class="flex flex-col">
<span
                                        class="font-medium text-n-slate-12"
                                        v-dompurify-html="formatContactName(conversation.contact_name || 'Sem nome')"
                                    ></span>
                                    <div v-if="conversation.ai_interactions > 0">
                                        <span class="text-sm text-n-brand">
                                            {{ conversation.ai_interactions }} {{ conversation.ai_interactions === 1 ? 'interação' : 'interações' }}
                                        </span>
                                    </div>
                                    <div v-else-if="conversation.display_id" class="text-xs text-n-slate-10 mt-1">
                                        Conversa #{{ conversation.display_id }}
                                    </div>
                                </div>
                                <div>
                                    <i class="i-lucide-external-link text-slate-600 dark:text-slate-400 text-base flex-shrink-0"></i>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <div
                class="flex justify-center items-center p-1 border-t-2 border-n-slate-4 dark:border-n-slate-7 flex-shrink-0">
                
                <!-- Loading indicator for infinite scroll -->
                <div v-if="loading" class="text-center py-2 text-n-slate-11 text-sm">
                    Carregando conversas...
                </div>

                <!-- End of list indicator -->
                <div v-if="hasReachedEnd && displayedConversations.length > 0"
                    class="text-center py-2 text-n-slate-11 text-sm">
                    Todas as conversas foram carregadas
                </div>

                <!-- Empty state -->
                <div v-if="displayedConversations.length === 0 && !loading" class="text-center py-2 text-n-slate-11 text-sm">
                    Nenhuma conversa encontrada
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { formatFlagDisplayText } from 'shared/helpers/flagEmoji';

const props = defineProps({
    conversations: {
        type: Array,
        required: true,
        default: () => []
    },
    show: {
        type: Boolean,
        default: false,
    },
    title: {
        type: String,
        default: 'Conversas',
    },
    loading: {
        type: Boolean,
        default: false,
    },
    subtitle: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['close']);

// Composables
const { accountId } = useAccount();

// Refs
const conversationList = ref(null);
const currentPage = ref(1);
const pageSize = 25;
const isLoadingMore = ref(false);

// Computed
const totalPages = computed(() => {
    return Math.ceil(props.conversations.length / pageSize);
});

const displayedConversations = computed(() => {
    const endIndex = currentPage.value * pageSize;
    return props.conversations.slice(0, endIndex);
});

const hasReachedEnd = computed(() => {
    return currentPage.value >= totalPages.value;
});

const formatContactName = name => formatFlagDisplayText(name);

// Methods
const closeModal = () => {
    emit('close');
};

const openConversation = (conversationDisplayId) => {
    const path = frontendURL(
        conversationUrl({
            accountId: accountId.value,
            id: conversationDisplayId,
        })
    );
    window.open(path, '_blank');
};

const loadMoreConversations = () => {
    if (isLoadingMore.value || hasReachedEnd.value) return;

    isLoadingMore.value = true;

    // Simulate loading delay for better UX
    setTimeout(() => {
        currentPage.value++;
        isLoadingMore.value = false;
    }, 300);
};

const handleScroll = (event) => {
    const { target } = event;
    const threshold = 100; // Load more when 100px from bottom

    if (target.scrollTop + target.clientHeight >= target.scrollHeight - threshold) {
        loadMoreConversations();
    }
};

const resetPagination = () => {
    currentPage.value = 1;
    isLoadingMore.value = false;

    // Reset scroll position
    nextTick(() => {
        if (conversationList.value) {
            conversationList.value.scrollTop = 0;
        }
    });
};

// Watchers
watch(() => props.conversations, () => {
    resetPagination();
}, { immediate: true });

watch(() => props.show, (isVisible) => {
    if (isVisible) {
        resetPagination();
    }
});
</script>

<style scoped>
/* Only custom styles that can't be done with Tailwind */
.modal-backdrop {
    backdrop-filter: blur(4px);
}

/* Barra de rolagem personalizada para o container principal */
.custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
    width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    transition: background-color 0.3s ease;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.3);
}

.custom-scrollbar::-webkit-scrollbar-thumb:active {
    background-color: rgba(0, 0, 0, 0.4);
}

/* Tema escuro */
.dark .custom-scrollbar {
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.3);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:active {
    background-color: rgba(255, 255, 255, 0.4);
}
</style>
